/**
 * Time-Machine (Attio leg) — reconstruct a record's change history from the
 * governance activity ledger.
 *
 * WHY: Attio's differentiator is field-level history + provenance. Argo
 * already has the raw material — every governed action (proposed / approved /
 * rejected / executed / undone) and every agent run lands in the append-only
 * activity ledger, keyed to its record via `subjectId`. This module is a PURE
 * first cut over that ledger: given the entries and a subject id, it answers
 * "what governed actions touched this record, when, and by whom?".
 *
 * PURE by contract: functions read the entries passed in, never the store,
 * and never mutate their inputs. Do NOT change how writes are logged — this
 * is reconstruction only, so an empty timeline honestly means the record was
 * never touched by a governed action, never a fabricated history.
 */

import type { ActivityEntry } from '../db/types';

/** Who drove a governed event — the provenance axis Attio buyers care about. */
export type TimelineActor = 'agent' | 'human';

/** One chronological step in a record's reconstructed history. */
export interface TimelineEvent {
  /** ISO timestamp of the event. */
  at: string;
  /** The governed event type (proposed / approved / executed / ...). */
  event: ActivityEntry['event'];
  /** Derived provenance: source==='agent' → 'agent', everything else 'human'. */
  actor: TimelineActor;
  /** The ledger's human-readable one-liner. */
  summary: string;
}

/** Aggregate provenance for a record: how much history, from whom, how fresh. */
export interface ProvenanceSummary {
  /** Total governed events touching the record. */
  totalEvents: number;
  /** Split of those events by derived actor. Always both keys, zero-filled. */
  byActor: Record<TimelineActor, number>;
  /** ISO timestamp of the most recent event, or null when untouched. */
  lastTouched: string | null;
}

/**
 * Derive the actor from the ledger's `source`. Only a true agent origin maps
 * to 'agent'; chat / scheduler / autopilot / system all represent actions a
 * human initiated or the system performed under human governance, so for this
 * first cut they read as 'human'. Exported so UI surfaces (the Decision Ledger)
 * badge provenance with the exact same rule the timeline uses — no drift.
 */
export function deriveActor(entry: ActivityEntry): TimelineActor {
  return entry.source === 'agent' ? 'agent' : 'human';
}

/** Back-compat internal alias. */
const actorOf = deriveActor;

/**
 * The chronological (oldest-first) list of governed events touching
 * `subjectId`. Pure over `entries`; empty array for an untouched subject.
 */
export function recordTimeline(entries: ActivityEntry[], subjectId: string): TimelineEvent[] {
  return entries
    .filter((e) => e.subjectId === subjectId)
    .slice()
    .sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0))
    .map((e) => ({ at: e.at, event: e.event, actor: actorOf(e), summary: e.summary }));
}

/**
 * Aggregate provenance for `subjectId`: event count, agent/human split, and
 * the most recent touch. Honest empty — totals of 0 and lastTouched null —
 * for a subject with no governed history.
 */
export function recordProvenanceSummary(entries: ActivityEntry[], subjectId: string): ProvenanceSummary {
  const timeline = recordTimeline(entries, subjectId);
  const byActor: Record<TimelineActor, number> = { agent: 0, human: 0 };
  for (const t of timeline) byActor[t.actor] += 1;
  return {
    totalEvents: timeline.length,
    byActor,
    lastTouched: timeline.length > 0 ? timeline[timeline.length - 1].at : null,
  };
}

/**
 * Whole-ledger provenance: the same summary shape as recordProvenanceSummary
 * but aggregated across EVERY entry (all subjects), for the Decision Ledger's
 * header KPIs — total governed events, the agent/human split, and the most
 * recent touch. Pure over `entries`; honest zeros for an empty ledger.
 */
export function ledgerProvenance(entries: ActivityEntry[]): ProvenanceSummary {
  const byActor: Record<TimelineActor, number> = { agent: 0, human: 0 };
  let lastTouched: string | null = null;
  for (const e of entries) {
    byActor[deriveActor(e)] += 1;
    if (lastTouched === null || e.at > lastTouched) lastTouched = e.at;
  }
  return { totalEvents: entries.length, byActor, lastTouched };
}
